import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import FormInput from "../../../components/controls/FormInput";
import CitySelector from "../../../components/dropdowns/CitySelector";
import { useAuth } from "../../../contexts/AuthContext";
import FormTextarea from "../../../components/controls/FormTextArea";
import Modal from "../../../components/controls/Modal";
import Button from "../../../components/controls/Button";
import Toggle from "../../../components/ui/Toggle";

export default function CreateCompanyModal({
  isOpen,
  onClose,
  cityId,
  onCompanyCreated,
  cityIdChanged,
}) {
  const { t } = useTranslation();
  const { currentUser } = useAuth();

  const [selectedCityId, setSelectedCityId] = useState(cityId || null);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [assignSelf, setAssignSelf] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedCityId(cityId ?? null);
    setName("");
    setNote("");
    setAssignSelf(true);
    cityIdChanged?.(cityId ?? null);
  }, [isOpen, cityId, cityIdChanged]);

  const handleCreate = async () => {
    if (!selectedCityId || !name?.trim()) return;
    setSaving(true);

    try {
      const payload = {
        name: name.trim(),
        cityId: selectedCityId,
        createdBy: currentUser?.id,
        assignedTo: assignSelf ? currentUser?.id : null,
        note: note.trim() || null,
      };

      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Representative/CompaniesToContact`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || "Erreur lors de la création");
      }

      const created = await res.json().catch(() => null);
      onCompanyCreated?.(created || payload);
      onClose?.();
    } catch (err) {
      alert(err?.message || "Erreur lors de la création");
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        color="secondary"
        size="sm"
        onClick={onClose}
        disabled={saving}
      >
        {t("global.cancel")}
      </Button>
      <Button
        type="button"
        color="primary"
        size="sm"
        onClick={handleCreate}
        disabled={saving}
      >
        {t("global.save")}
      </Button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("reservation.addCompany")}
      footer={footer}
      size="md"
      showCloseButton
    >
      <div className="text-primary space-y-4">
        <CitySelector value={selectedCityId} onChange={setSelectedCityId} />

        <FormInput
          name="name"
          label={t("reservation.companyName")}
          value={name}
          onChange={(field, value) => setName(value)}
          required
        />

        <FormTextarea
          value={note}
          label={t("global.note")}
          name="note"
          onChange={(field, value) => setNote(value)}
          placeholder={"Ex: # Téléphone, Email, Adresse, lien Facebook..."}
          rows={3}
          maxLength={500}
          showMaxLength
          required
        />

        <div className="flex justify-start">
          <Toggle
            isEnabled={assignSelf}
            onToggle={(e) => setAssignSelf(e.target.checked)}
            labelOn={t("reservation.assignToMe")}
            labelOff={t("reservation.assignToMe")}
            colorActive="bg-green-500"
            colorInactive="bg-gray-400"
            className="toggle-lg"
            title={t("reservation.assignToMe")}
            name="assignSelf"
          />
        </div>
      </div>
    </Modal>
  );
}
